/**
 * "My leave" — the self-service half of `/hr/leave`, WP-W7.
 * `web/shapes/hr-leave.md` §5 is the agreed shape.
 *
 * Three independent regions (`PRODUCT.md` §4): the caller's own **balances**, a
 * **request-leave** form, and **my requests** with a cancel action. Both codes behind
 * them (`hr.request_leave`, `hr.view_leave`) are on the self-service floor, so all three
 * fill for an ordinary employee — but they stay independent on purpose, because a
 * session whose `hr.view_leave` was explicitly denied can still file a request while the
 * balances and my-requests regions each render their own `403` in place
 * (`web/shapes/hr-leave.md` §3).
 *
 * The request form has **no employee-id field**. There is nothing to omit-when-blank
 * here: the field does not exist on `LeaveRequestCreateRequest` at all, because the
 * subject is the session (`api/hr.ts`'s own docblock on the type).
 */

import { useMemo, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatDay } from '../../i18n/datetime';
import type {
  LeaveBalance,
  LeaveRequestCreateRequest,
  LeaveRequestRecord,
  LeaveType,
} from '../../api/hr';
import {
  LEAVE_REQUEST_STATUS_BADGE,
  LEAVE_REQUEST_STATUS_LABEL,
  isPendingLeaveRequest,
} from '../../hr/leave';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { DateField } from '../../components/DateField';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { SEVERITY_ICON } from '../../states/icons';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';

export interface MyLeavePanelProps {
  readonly typesStatus: QueryStatus;
  readonly types: readonly LeaveType[] | null;
  readonly typesError: WorkforceApiError | null;

  readonly balancesStatus: QueryStatus;
  readonly balances: readonly LeaveBalance[] | null;
  readonly balancesError: WorkforceApiError | null;
  readonly onReloadBalances: () => void;

  readonly requestPending: boolean;
  readonly requestError: WorkforceApiError | null;
  readonly requestCreated: LeaveRequestRecord | null;
  readonly onSubmitRequest: (body: LeaveRequestCreateRequest) => void;

  readonly requestsStatus: QueryStatus;
  readonly requests: readonly LeaveRequestRecord[] | null;
  readonly requestsError: WorkforceApiError | null;
  readonly onReloadRequests: () => void;

  readonly cancellingId: string | null;
  readonly cancelError: WorkforceApiError | null;
  readonly onCancel: (requestId: string) => void;
}

export function MyLeavePanel(props: MyLeavePanelProps): ReactNode {
  const { t, locale } = useI18n();
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');

  const typeName = useMemo(() => {
    const map = new Map<string, string>();
    for (const type of props.types ?? []) map.set(type.id, type.name);
    return map;
  }, [props.types]);

  const ready = leaveTypeId !== '' && startDate.trim() !== '' && endDate.trim() !== '';

  const activeTypes = (props.types ?? []).filter((type) => type.is_active);

  return (
    <section className="wp-section" aria-labelledby="w7-my-heading" data-testid="w7-my">
      <h2 className="wp-section__title" id="w7-my-heading">
        {t('hr.leave.my.title')}
      </h2>

      {/* ── Balances ────────────────────────────────────────────────────────── */}
      <div className="wp-section__subsection" data-testid="w7-balances">
        <h3 className="wp-section__title">{t('hr.leave.balances.title')}</h3>
        {props.balancesStatus === 'loading' ? (
          <Skeleton
            rows={2}
            variant="block"
            label={t('hr.leave.balances.subject')}
            testId="w7-balances-skeleton"
          />
        ) : null}
        {props.balancesStatus === 'error' && props.balancesError !== null ? (
          <ApiErrorNote
            error={props.balancesError}
            onRetry={props.onReloadBalances}
            subject={t('hr.leave.balances.subject')}
            testId="w7-balances-error"
          />
        ) : null}
        {props.balances === null ? null : props.balances.length === 0 ? (
          <EmptyState
            title={t('hr.leave.balances.empty.title')}
            description={t('hr.leave.balances.empty.body')}
            testId="w7-balances-empty"
          />
        ) : (
          /*
           * A balance is four comparable figures per leave type, so it is a table and
           * not a sentence: the three day counts sat glued into one muted line
           * ("12 ngày cấp · 2 ngày đã dùng · 10 ngày còn lại"), which is the shape
           * Luật Số Thẳng Cột exists to forbid — nothing in it lines up down the
           * column. `.wp-num` puts them on tabular figures, right-aligned, and the one
           * an employee opened this screen to read — what is left — carries the weight.
           */
          <div className="wp-table-scroll">
            <table className="wp-table wp-f-figures" data-testid="w7-balances-list">
              <thead>
                <tr>
                  <th scope="col">{t('hr.leave.balances.col.type')}</th>
                  <th scope="col" className="wp-num">
                    {t('hr.leave.balances.col.year')}
                  </th>
                  <th scope="col" className="wp-num">
                    {t('hr.leave.balances.col.accrued')}
                  </th>
                  <th scope="col" className="wp-num">
                    {t('hr.leave.balances.col.used')}
                  </th>
                  <th scope="col" className="wp-num">
                    {t('hr.leave.balances.col.remaining')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {props.balances.map((balance) => (
                  <tr key={balance.id} data-testid={`w7-balance-${balance.id}`}>
                    <th scope="row">
                      {typeName.get(balance.leave_type_id) ?? t('hr.leave.type.unknown')}
                    </th>
                    <td className="wp-num">{balance.year}</td>
                    <td className="wp-num">{balance.accrued_days}</td>
                    <td className="wp-num">{balance.used_days}</td>
                    <td className="wp-num wp-f-figures__lead">{balance.remaining_days}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Request leave ──────────────────────────────────────────────────── */}
      <div className="wp-section__subsection" data-testid="w7-request">
        <h3 className="wp-section__title">{t('hr.leave.request.title')}</h3>
        <p className="wp-small wp-muted">{t('hr.leave.request.intro')}</p>
        <form
          className="wp-filters"
          onSubmit={(event) => {
            event.preventDefault();
            if (!ready || props.requestPending) return;
            const trimmedReason = reason.trim();
            props.onSubmitRequest({
              leave_type_id: leaveTypeId,
              start_date: startDate,
              end_date: endDate,
              ...(trimmedReason === '' ? {} : { reason: trimmedReason }),
            });
          }}
        >
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w7-request-type">
              {t('hr.leave.request.type')}
            </label>
            <select
              className="wp-field__input"
              id="w7-request-type"
              data-testid="w7-request-type"
              value={leaveTypeId}
              onChange={(event) => {
                setLeaveTypeId(event.target.value);
              }}
            >
              <option value="">{t('hr.leave.request.typePlaceholder')}</option>
              {activeTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
          <DateField
            className="wp-filters__group"
            id="w7-request-start"
            testId="w7-request-start"
            label={t('hr.leave.request.startDate')}
            value={startDate}
            onChange={setStartDate}
          />
          <DateField
            className="wp-filters__group"
            id="w7-request-end"
            testId="w7-request-end"
            label={t('hr.leave.request.endDate')}
            value={endDate}
            onChange={setEndDate}
          />
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w7-request-reason">
              {t('hr.leave.request.reason')}
            </label>
            <input
              className="wp-field__input"
              id="w7-request-reason"
              data-testid="w7-request-reason"
              type="text"
              value={reason}
              maxLength={2000}
              autoComplete="off"
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
          </div>
          <div className="wp-filters__actions">
            <button
              className="wp-button wp-button--primary"
              type="submit"
              data-testid="w7-request-submit"
              disabled={!ready || props.requestPending}
            >
              {props.requestPending
                ? t('hr.leave.request.submitting')
                : t('hr.leave.request.submit')}
            </button>
          </div>
        </form>
        {props.requestError === null ? null : (
          <ApiErrorNote
            error={props.requestError}
            subject={t('hr.leave.request.subject')}
            testId="w7-request-error"
          />
        )}
        {props.requestCreated === null ? null : (
          <StatusNote
            severity="low"
            title={t('hr.leave.request.ok', { days: props.requestCreated.days })}
            testId="w7-request-ok"
          />
        )}
        {props.typesError === null ? null : (
          <ApiErrorNote
            error={props.typesError}
            subject={t('hr.leave.types.subject')}
            testId="w7-types-error"
          />
        )}
      </div>

      {/* ── My requests ─────────────────────────────────────────────────────── */}
      <div className="wp-section__subsection" data-testid="w7-my-requests">
        <h3 className="wp-section__title">{t('hr.leave.myRequests.title')}</h3>
        {props.requestsStatus === 'loading' ? (
          <Skeleton
            rows={3}
            variant="block"
            label={t('hr.leave.myRequests.subject')}
            testId="w7-my-requests-skeleton"
          />
        ) : null}
        {props.requestsStatus === 'error' && props.requestsError !== null ? (
          <ApiErrorNote
            error={props.requestsError}
            onRetry={props.onReloadRequests}
            subject={t('hr.leave.myRequests.subject')}
            testId="w7-my-requests-error"
          />
        ) : null}
        {props.requests === null ? null : props.requests.length === 0 ? (
          <EmptyState
            title={t('hr.leave.myRequests.empty.title')}
            description={t('hr.leave.myRequests.empty.body')}
            testId="w7-my-requests-empty"
          />
        ) : (
          <ul className="wp-rows" data-testid="w7-my-requests-list">
            {props.requests.map((request) => {
              const badge = LEAVE_REQUEST_STATUS_BADGE[request.status];
              const cancelling = props.cancellingId === request.id;
              return (
                <li className="wp-row" key={request.id} data-testid={`w7-my-request-${request.id}`}>
                  <div className="wp-row__main">
                    <p>
                      <Badge
                        severity={badge}
                        icon={SEVERITY_ICON[badge]}
                        label={t(LEAVE_REQUEST_STATUS_LABEL[request.status])}
                        testId={`w7-my-request-status-${request.id}`}
                      />{' '}
                      <span>
                        {typeName.get(request.leave_type_id) ?? t('hr.leave.type.unknown')}
                      </span>
                    </p>
                    <p className="wp-small wp-muted">
                      {t('hr.leave.myRequests.row.meta', {
                        start: formatDay(request.start_date, locale, {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        }),
                        end: formatDay(request.end_date, locale, {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        }),
                        days: request.days,
                      })}
                    </p>
                    <p className="wp-small wp-muted">
                      {request.reason === null
                        ? t('hr.leave.myRequests.row.noReason')
                        : t('hr.leave.myRequests.row.reason', { reason: request.reason })}
                    </p>
                  </div>
                  <div className="wp-row__figure">
                    {isPendingLeaveRequest(request.status) ? (
                      <button
                        className="wp-button"
                        type="button"
                        data-testid={`w7-my-request-cancel-${request.id}`}
                        disabled={cancelling}
                        onClick={() => {
                          props.onCancel(request.id);
                        }}
                      >
                        {cancelling
                          ? t('hr.leave.myRequests.cancelling')
                          : t('hr.leave.myRequests.cancel')}
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {props.cancelError === null ? null : (
          <ApiErrorNote
            error={props.cancelError}
            subject={t('hr.leave.myRequests.subject')}
            testId="w7-my-request-cancel-error"
          />
        )}
      </div>
    </section>
  );
}
